/**
 * WebGL Resource Manager
 * Automatic resource tracking and disposal for WebGL applications
 */

import * as THREE from 'three';
import { webglLogger } from '@/lib/utils/logger';

export interface ResourceStats {
  geometries: number;
  materials: number;
  textures: number;
  totalMemoryEstimate: number; // in MB
}

export interface ResourceMetadata {
  type: string;
  createdAt: number;
  stackTrace?: string;
}

/**
 * Automatic resource manager for WebGL applications
 * Uses WeakMap for automatic cleanup when objects are garbage collected
 */
export class ResourceManager {
  private geometries = new Set<THREE.BufferGeometry>();
  private materials = new Set<THREE.Material>();
  private textures = new Set<THREE.Texture>();
  private disposables = new Set<{ dispose(): void }>();
  
  // Track creation metadata for debugging
  private resourceMetadata = new WeakMap<object, ResourceMetadata>();

  private isDisposed = false;

  constructor(private debugMode = false) {}

  /**
   * Track a geometry with automatic disposal
   */
  trackGeometry<T extends THREE.BufferGeometry>(geometry: T): T {
    if (this.isDisposed) {
      webglLogger.warn('Attempting to track geometry on disposed ResourceManager');
      return geometry;
    }

    this.geometries.add(geometry);
    
    if (this.debugMode) {
      this.resourceMetadata.set(geometry, {
        type: 'BufferGeometry',
        createdAt: Date.now(),
        stackTrace: new Error().stack
      });
    }

    return geometry;
  }

  /**
   * Track a material with automatic disposal
   */
  trackMaterial<T extends THREE.Material>(material: T): T {
    if (this.isDisposed) {
      webglLogger.warn('Attempting to track material on disposed ResourceManager');
      return material;
    }

    this.materials.add(material);
    
    if (this.debugMode) {
      this.resourceMetadata.set(material, {
        type: material.type,
        createdAt: Date.now(),
        stackTrace: new Error().stack
      });
    }

    return material;
  }

  /**
   * Track a texture with automatic disposal
   */
  trackTexture<T extends THREE.Texture>(texture: T): T {
    if (this.isDisposed) {
      webglLogger.warn('Attempting to track texture on disposed ResourceManager');
      return texture;
    }

    this.textures.add(texture);
    
    if (this.debugMode) {
      this.resourceMetadata.set(texture, {
        type: 'Texture',
        createdAt: Date.now(),
        stackTrace: new Error().stack
      });
    }

    return texture;
  }

  /**
   * Track any disposable resource
   */
  trackDisposable<T extends { dispose(): void }>(resource: T): T {
    if (this.isDisposed) {
      webglLogger.warn('Attempting to track disposable on disposed ResourceManager');
      return resource;
    }

    this.disposables.add(resource);
    
    if (this.debugMode) {
      this.resourceMetadata.set(resource, {
        type: 'Disposable',
        createdAt: Date.now(),
        stackTrace: new Error().stack
      });
    }

    return resource;
  }

  /**
   * Remove a resource from tracking (useful when manually disposing)
   */
  untrack(resource: THREE.BufferGeometry | THREE.Material | THREE.Texture | { dispose(): void }): void {
    if (resource instanceof THREE.BufferGeometry) {
      this.geometries.delete(resource);
    } else if (resource instanceof THREE.Material) {
      this.materials.delete(resource);
    } else if (resource instanceof THREE.Texture) {
      this.textures.delete(resource);
    } else if ('dispose' in resource) {
      this.disposables.delete(resource);
    }
    
    this.resourceMetadata.delete(resource);
  }

  /**
   * Get current resource statistics
   */
  getStats(): ResourceStats {
    const geometryMemory = Array.from(this.geometries).reduce((total, geo) => {
      // Rough estimate: vertices * attributes * 4 bytes per float
      const positions = geo.attributes.position?.count || 0;
      const attributeCount = Object.keys(geo.attributes).length;
      return total + (positions * attributeCount * 4) / (1024 * 1024);
    }, 0);

    const textureMemory = Array.from(this.textures).reduce((total, tex) => {
      // Rough estimate: width * height * channels * bytes per channel
      const width = (tex.image as HTMLImageElement | null)?.width || 0;
      const height = (tex.image as HTMLImageElement | null)?.height || 0;
      const channels = 4; // Assume RGBA
      const bytesPerChannel = 1; // Assume 8-bit
      return total + (width * height * channels * bytesPerChannel) / (1024 * 1024);
    }, 0);

    return {
      geometries: this.geometries.size,
      materials: this.materials.size,
      textures: this.textures.size,
      totalMemoryEstimate: geometryMemory + textureMemory
    };
  }

  /**
   * Dispose all tracked resources
   */
  dispose(): void {
    if (this.isDisposed) {
      webglLogger.warn('ResourceManager already disposed');
      return;
    }

    const startTime = performance.now();
    let disposedCount = 0;

    try {
      // Dispose geometries
      this.geometries.forEach(geometry => {
        try {
          geometry.dispose();
          disposedCount++;
        } catch (error) {
          webglLogger.warn('Failed to dispose geometry:', error);
        }
      });

      // Dispose materials
      this.materials.forEach(material => {
        try {
          material.dispose();
          disposedCount++;
        } catch (error) {
          webglLogger.warn('Failed to dispose material:', error);
        }
      });

      // Dispose textures
      this.textures.forEach(texture => {
        try {
          texture.dispose();
          disposedCount++;
        } catch (error) {
          webglLogger.warn('Failed to dispose texture:', error);
        }
      });

      // Dispose other resources
      this.disposables.forEach(resource => {
        try {
          resource.dispose();
          disposedCount++;
        } catch (error) {
          webglLogger.warn('Failed to dispose resource:', error);
        }
      });

      // Clear all collections
      this.geometries.clear();
      this.materials.clear();
      this.textures.clear();
      this.disposables.clear();

      const endTime = performance.now();
      
      if (this.debugMode) {
        webglLogger.info(`ResourceManager disposed ${disposedCount} resources in ${(endTime - startTime).toFixed(2)}ms`);
      }

    } catch (error) {
      webglLogger.error('Error during ResourceManager disposal:', error);
    } finally {
      this.isDisposed = true;
    }
  }

  /**
   * Check if the resource manager has been disposed
   */
  get disposed(): boolean {
    return this.isDisposed;
  }

  /**
   * Get debug information about tracked resources
   */
  getDebugInfo(): Array<{ resource: object; metadata: ResourceMetadata | undefined }> {
    if (!this.debugMode) {
      return [];
    }

    const info: Array<{ resource: object; metadata: ResourceMetadata | undefined }> = [];
    
    [...this.geometries, ...this.materials, ...this.textures, ...this.disposables].forEach(resource => {
      const metadata = this.resourceMetadata.get(resource);
      if (metadata) {
        info.push({ resource, metadata });
      }
    });

    return info;
  }
}

/**
 * Global resource manager instance for shared resources
 */
export const globalResourceManager = new ResourceManager(process.env.NODE_ENV === 'development');